import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { Printer, Download, ArrowLeft } from 'lucide-react';
import { useHiddenStudentRoute } from '../../utils/studentRoutes';

function AttendanceUndertaking() {
  const { rollNumber, navigate } = useHiddenStudentRoute('/student/attendance-undertaking');
  const printRef = useRef();
  const [student, setStudent] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!rollNumber) {
      setLoading(false);
      return;
    }
    loadStudentData();
  }, [rollNumber]);

  const loadStudentData = async () => {
    try {
      // Load student profile
      const studentDoc = await getDoc(doc(db, 'students', rollNumber));
      if (studentDoc.exists()) {
        setStudent(studentDoc.data());
      }

      // Load attendance
      const attendanceDoc = await getDoc(doc(db, 'attendance', rollNumber));
      if (attendanceDoc.exists()) {
        setAttendance(attendanceDoc.data());
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading student data:', error);
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  const getCurrentMonth = () => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[new Date().getMonth()];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading undertaking form...</p>
        </div>
      </div>
    );
  }

  if (!student || !attendance) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 font-semibold">Unable to load student data</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const fullName = `${student.firstName || ''} ${student.fatherName || ''} ${student.surname || ''}`.trim();

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      {/* Action Buttons - Hidden when printing */}
      <div className="max-w-4xl mx-auto px-4 mb-6 no-print">
        <div className="flex items-center justify-between bg-white rounded-lg shadow-md p-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
            <button
              onClick={handleDownloadPDF}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* A4 Page Container */}
      <div className="max-w-4xl mx-auto bg-white shadow-lg print-container">
        <div ref={printRef} className="a4-page">
          {/* Letterhead */}
          <div className="letterhead mb-4">
            <img 
              src="/ybit-letterhead.png" 
              alt="YBIT Letterhead" 
              className="w-full"
            />
          </div>

          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="text-xl font-bold" style={{ textDecoration: 'underline' }}>
              Undertaking from the Parents Regarding 75% Attendance
            </h1>
          </div>

          {/* Form Content */}
          <div className="content-section">
            {/* Parent/Guardian Information */}
            <div className="mb-4">
              <p className="text-base leading-relaxed">
                I, <span className="underline-text-blank"></span>
              </p>
            </div>

            <div className="mb-4">
              <p className="text-base leading-relaxed">
                Father/Mother/Guardian of <span className="font-semibold underline-text">{fullName}</span>
              </p>
            </div>

            {/* Student Details */}
            <div className="mb-4">
              <p className="text-base leading-relaxed">
                studying in class - <span className="font-semibold">First Year</span> 
                {' '}Div-<span className="font-semibold underline-text">{student.division || '______'}</span>
                {' '}Roll No:<span className="font-semibold underline-text">{rollNumber}</span>
                {' '}Branch: <span className="font-semibold underline-text">{student.branch || '______________'}</span>
              </p>
            </div>

            {/* Attendance Details */}
            <div className="mb-6">
              <p className="text-base leading-relaxed">
                Percentage of Attendance: <span className="font-semibold text-red-600">
                  {attendance.overallPercentage ? attendance.overallPercentage.toFixed(2) : '0'}%
                </span>
                {' '}Month: <span className="font-semibold underline-text"></span>
              </p>
            </div>

            {/* Undertaking Points */}
            <div className="mb-8">
              <p className="text-base mb-4">hereby undertake:</p>
              
              <ol className="list-decimal space-y-5 text-base" style={{ paddingLeft: '1.5rem' }}>
                <li className="leading-relaxed text-justify" style={{ paddingLeft: '0.5rem' }}>
                  I am aware that as per the Mumbai University norms <span className="font-bold">minimum 75% attendance is compulsory.</span>
                </li>
                <li className="leading-relaxed text-justify" style={{ paddingLeft: '0.5rem' }}>
                  I am also aware that if the attendance of my ward is less than the norms as specified above, his/her <span className="font-bold">term will not be granted</span> and he/she may <span className="font-bold">not be permitted to appear</span> for Mumbai University examination.
                </li>
                <li className="leading-relaxed text-justify" style={{ paddingLeft: '0.5rem' }}>
                  I confirm that <span className="font-bold">my ward is physically & mentally fit</span> to attend this course and is in position to cope up with the course requirement.
                </li>
              </ol>
            </div>

            {/* Signature Section */}
            <div className="signature-section mt-10">
              <div className="grid grid-cols-2 gap-12">
                {/* Left Column - Date */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-base whitespace-nowrap">Date-</span>
                    <div className="border-b border-black flex-1 h-6"></div>
                  </div>
                </div>

                {/* Right Column - Parent Signature */}
                <div className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <span className="text-base whitespace-nowrap">Parent's Sign:</span>
                    <div className="border-b border-black flex-1 h-6"></div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-base whitespace-nowrap">Name:</span>
                    <div className="border-b border-black flex-1 h-6"></div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-base whitespace-nowrap">Designation:</span>
                    <div className="border-b border-black flex-1 h-6"></div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-base whitespace-nowrap">Contact Nos:</span>
                    <div className="border-b border-black flex-1 h-6"></div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-base whitespace-nowrap">Address:</span>
                    <div className="flex-1">
                      <div className="border-b border-black h-6 mb-2"></div>
                      <div className="border-b border-black h-6 mb-2"></div>
                      <div className="border-b border-black h-6"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Student Signature Row */}
              <div className="grid grid-cols-2 gap-12 mt-12">
                <div className="flex items-center space-x-2">
                  <span className="text-base">Date:</span>
                  <div className="border-b border-black flex-1 h-6"></div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-base whitespace-nowrap">Name & Signature of Student</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="footer-section mt-6 pt-3 border-t-2 border-black">
            <p className="text-xs text-center leading-tight">
              Bhonsale Knowledge City Building No.1, Vazarwadi, Charathe, Tal: Sawantwadi Dist:Sindhudurg 416510
            </p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          
          .no-print {
            display: none !important;
          }
          
          .print-container {
            box-shadow: none !important;
            margin: 0 !important;
          }
          
          .a4-page {
            width: 210mm;
            min-height: 297mm;
            padding: 20mm 25mm;
            margin: 0;
            box-sizing: border-box;
            page-break-after: always;
          }
          
          .letterhead img {
            max-height: 110px;
            display: block;
            margin: 0 auto;
          }
        }
        
        @media screen {
          .a4-page {
            width: 210mm;
            min-height: 297mm;
            padding: 20mm 25mm;
            margin: 0 auto;
            box-sizing: border-box;
            background: white;
          }
          
          .print-container {
            margin-bottom: 2rem;
          }
        }
        
        .underline-text {
          border-bottom: 1.5px solid #000;
          padding: 0 6px 1px 6px;
          display: inline-block;
          min-width: 80px;
        }
        
        .underline-text-blank {
          border-bottom: 1.5px solid #000;
          padding: 0 6px 1px 6px;
          display: inline-block;
          min-width: 300px;
        }
        
        .content-section {
          font-size: 15px;
          line-height: 1.8;
          text-align: justify;
        }
        
        .content-section p {
          margin-bottom: 0.75rem;
        }
        
        .content-section ol li {
          text-align: justify;
          line-height: 1.8;
        }
        
        .signature-section {
          font-size: 14px;
          margin-top: 3rem;
        }
        
        .footer-section {
          font-size: 10px;
        }
        
        .letterhead {
          max-height: 130px;
          overflow: hidden;
          margin-bottom: 1rem;
        }
        
        .letterhead img {
          max-height: 130px;
          width: 100%;
          object-fit: contain;
        }
        
        h1 {
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 2rem;
        }
      `}</style>
    </div>
  );
}

export default AttendanceUndertaking;
